import { CascadiaColor, CascadiaStandardIcon } from "@certn/cascadia-tokens";
import { css, cx } from "@emotion/css";

import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";

export const SectionMessageTypes = [
  "default",
  "success",
  "info",
  "warning",
  "danger",
  "promotion",
] as const;

export type SectionMessageType = (typeof SectionMessageTypes)[number];

export interface SectionMessageProps {
  sectionTitle?: string;
  children?: React.ReactNode;
  icon?: CascadiaStandardIcon;
  type?: SectionMessageType;
  fontSize?: "sm" | "md";
  testID?: string;
  noBorderRadius?: boolean;
  noPadding?: boolean;
  className?: string;
  actions?: React.ReactNode;
}

export function SectionMessage(props: SectionMessageProps) {
  const {
    sectionTitle,
    children,
    icon,
    type = "default",
    testID = "section-message",
    fontSize = "sm",
    noBorderRadius,
    noPadding,
    className,
    actions,
  } = props;
  const iconColorsByType = {
    default: "icon-base",
    success: "icon-success",
    info: "icon-info",
    danger: "icon-danger",
    warning: "icon-warning",
    promotion: "icon-accent-purple",
  };
  const colorsByType = {
    default: "var(--background-accent-neutral-weak)",
    success: "var(--background-success-weak)",
    info: "var(--background-info-weak)",
    danger: "var(--background-danger-weak)",
    warning: "var(--background-warning-weak)",
    promotion: "var(--background-accent-purple-weakest)",
  };
  const styles = getStyles(noBorderRadius);

  return (
    <div
      className={cx(
        styles.sectionWrapper,
        css({
          background: noPadding ? undefined : colorsByType[type],
          padding: noPadding ? 0 : undefined,
          alignItems: children ? "flex-start" : "center",
        }),
        className
      )}
      data-testid={testID}
    >
      {icon && (
        <div
          className={cx(
            styles.sectionIconWrapper,
            css({ alignItems: children ? "flex-start" : "center" })
          )}
        >
          <Icon
            padding="space-05"
            name={icon}
            type="standard"
            color={iconColorsByType[type] as CascadiaColor}
          />
        </div>
      )}
      <div className={styles.contentWrapper}>
        {sectionTitle && (
          <Text
            as="div"
            style={
              fontSize === "md"
                ? "global/heading-medium"
                : "global/heading-small"
            }
          >
            {sectionTitle}
          </Text>
        )}
        {children && (
          <Text
            style={
              fontSize === "md"
                ? "global/body-primary"
                : "global/body-secondary"
            }
            as="div"
          >
            {children}
          </Text>
        )}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}

function getStyles(noBorderRadius?: boolean) {
  return {
    sectionWrapper: css({
      borderRadius: !noBorderRadius ? "var(--border-radius-2)" : undefined,
      padding: "var(--space-4)",
      display: "flex",
      boxSizing: "border-box",
    }),
    contentWrapper: css({
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-1)",
    }),
    sectionIconWrapper: css({
      display: "flex",
      marginRight: "var(--space-2)",
    }),
    actions: css({
      flex: 1,
      display: "flex",
      justifyContent: "flex-end",
    }),
  };
}
